import { useTranslation } from 'react-i18next'
import { CurecallButton, ECurecallButtonAppearance, ECurecallSize } from '@honestica/curecall-ui'
import { ECampaignType } from 'src/types/hr'

type TProps = Readonly<{
  campaignType: ECampaignType
  selectedCount: number
  message: string
  isSubmitting: boolean
  onConfirm: () => void
  onBack: () => void
}>

export const StepConfirmation = ({
  campaignType,
  selectedCount,
  message,
  isSubmitting,
  onConfirm,
  onBack,
}: TProps) => {
  const { t } = useTranslation()

  const typeLabel =
    campaignType === ECampaignType.PLAN_BLANC
      ? t('pages.hr.campaigns.new.plan-blanc-label')
      : t('pages.hr.campaigns.new.alert-label')

  const channelValue = campaignType === ECampaignType.PLAN_BLANC ? 'RCS, SMS' : 'SMS'

  const sendButtonLabel =
    campaignType === ECampaignType.PLAN_BLANC
      ? t('pages.hr.campaigns.new.confirmation.send-button-plan-blanc')
      : t('pages.hr.campaigns.new.confirmation.send-button-alert')

  const sendButtonAppearance =
    campaignType === ECampaignType.PLAN_BLANC
      ? ECurecallButtonAppearance.ALERT
      : ECurecallButtonAppearance.SECONDARY

  return (
    <div className="c-flex c-flex-col c-gap-6 c-max-w-2xl c-mx-auto c-w-full">
      <h2 className="c-font-semibold c-text-base">{t('pages.hr.campaigns.new.confirmation.title')}</h2>

      {/* Summary */}
      <div className="c-border c-border-grey-200 c-rounded-lg c-p-4 c-flex c-flex-col c-gap-3 c-bg-grey-50">
        <SummaryRow label={t('pages.hr.campaigns.new.message.summary-scenario')} value={typeLabel} />
        <SummaryRow
          label={t('pages.hr.campaigns.new.message.summary-recipients-label')}
          value={t('pages.hr.campaigns.new.message.summary-recipients-value', { count: selectedCount })}
        />
        <SummaryRow label={t('pages.hr.campaigns.new.message.summary-channel')} value={channelValue} />
        <div className="c-pt-2 c-border-t c-border-grey-200">
          <p className="c-text-xs c-font-semibold c-text-grey-500 c-mb-1">
            {t('pages.hr.campaigns.new.message.title')}
          </p>
          <p className="c-text-sm c-text-grey-800 c-whitespace-pre-wrap">{message}</p>
        </div>
      </div>

      {/* Warning */}
      <div className="c-rounded-lg c-bg-red-50 c-border c-border-red-200 c-p-4">
        <p className="c-text-sm c-text-red-700 c-font-medium">
          ⚠ {t('pages.hr.campaigns.new.confirmation.warning')}
        </p>
      </div>

      {/* Navigation */}
      <div className="c-flex c-items-center c-justify-between">
        <CurecallButton
          type="button"
          appearance={ECurecallButtonAppearance['PRIMARY-OUTLINED']}
          size={ECurecallSize.MEDIUM}
          onClick={onBack}
          isDisabled={isSubmitting}
        >
          {t('pages.hr.campaigns.new.confirmation.back-button')}
        </CurecallButton>

        <CurecallButton
          type="button"
          appearance={sendButtonAppearance}
          size={ECurecallSize.MEDIUM}
          isLoading={isSubmitting}
          onClick={onConfirm}
        >
          {sendButtonLabel}
        </CurecallButton>
      </div>
    </div>
  )
}

const SummaryRow = ({ label, value }: Readonly<{ label: string; value: string }>) => (
  <div className="c-flex c-items-center c-gap-2">
    <span className="c-text-xs c-font-semibold c-text-grey-500 c-w-24 c-shrink-0">{label}</span>
    <span className="c-text-sm c-text-grey-800">{value}</span>
  </div>
)
